import React, { useState } from 'react';
import { Role, useRole } from '../../context/RoleContext';
import { Icon, IconName } from '@tviklax/ui-components';
import {
  NavContainer,
  NavOverlay,
  NavContent,
  NavPrimaryAction,
  NavList,
  NavGroup,
  NavGroupLabel,
  NavItem,
  NavLink,
  NavIconWrapper,
  NavLabel,
  CollapseButton,
} from './Nav.styles';

interface NavProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  onNavigate: (tab: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

type MenuItem = { id: string; label: string; icon: IconName; requiredAction?: string; href?: string; roles?: Role[] };

const menuGroups: Array<{ label: string; items: MenuItem[] }> = [
  {
    label: 'Trade Operations',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
      { id: 'production', label: 'Production', icon: 'Building2' },
      { id: 'scheduling', label: 'Scheduling', icon: 'Calendar' },
      { id: 'exceptions', label: 'Exception Queue', icon: 'AlertTriangle', roles: ['admin', 'ops'] },
    ],
  },
  {
    label: 'Logistics',
    items: [
      { id: 'shipments', label: 'Shipments', icon: 'Truck' },
      { id: 'customs', label: 'Customs', icon: 'Shield' },
      { id: 'documents', label: 'Documents', icon: 'FileText' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { id: 'payments', label: 'Payments', icon: 'CreditCard' },
      { id: 'forex-compliance', label: 'Forex Compliance', icon: 'DollarSign' },
      { id: 'reconciliation', label: 'Reconciliation & Risk', icon: 'AlertCircle', requiredAction: 'recon.view' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { id: 'messaging', label: 'Messaging', icon: 'MessageCircle' },
      { id: 'contacts', label: 'Contacts', icon: 'User', roles: ['customer', 'supplier'] },
    ],
  },
  {
    label: 'Elsewhere on Tvikla',
    items: [
      { id: 'marketplace', label: 'Marketplace ↗', icon: 'ShoppingCart', href: 'https://test.tvikla.com', roles: ['customer'] },
      { id: 'seller-hub', label: 'Seller Hub ↗', icon: 'UserPlus', href: 'https://seller-test.tvikla.com', roles: ['supplier'] },
      { id: 'seller-panel', label: 'Seller Panel ↗', icon: 'Building2', href: 'https://sellerpanel-test.tvikla.com', roles: ['supplier'] },
    ],
  },
];

export const Nav: React.FC<NavProps> = ({
  isOpen,
  onClose,
  activeTab,
  onNavigate,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const { can, role } = useRole();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const handleNavigation = (tab: string) => {
    onNavigate(tab);
    onClose();
  };
  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <>
      <NavOverlay $isOpen={isOpen} onClick={onClose} />
      <NavContainer $isOpen={isOpen} $isCollapsed={isCollapsed}>
        {onToggleCollapse && (
          <CollapseButton onClick={onToggleCollapse}>
            <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
          </CollapseButton>
        )}
        <NavContent>
          <NavPrimaryAction
            $isCollapsed={isCollapsed}
            onClick={() => handleNavigation('create-order')}
            title={isCollapsed ? 'Create Order' : undefined}
          >
            <Icon name="Plus" size={18} />
            <NavLabel $isCollapsed={isCollapsed}>Create Order</NavLabel>
          </NavPrimaryAction>
          {menuGroups.map((group) => {
            const visibleItems = group.items.filter(item =>
              (!item.requiredAction || can(item.requiredAction as any)) &&
              (!item.roles || item.roles.includes(role))
            );
            if (visibleItems.length === 0) return null;
            const isGroupCollapsed = !isCollapsed && collapsedGroups.has(group.label);
            return (
              <NavGroup key={group.label}>
                <NavGroupLabel $isCollapsed={isCollapsed} onClick={() => toggleGroup(group.label)}>
                  {group.label}
                  <Icon name={isGroupCollapsed ? 'ChevronRight' : 'ChevronDown'} size={12} />
                </NavGroupLabel>
                {!isGroupCollapsed && (
                <NavList>
                  {visibleItems.map((item) => (
                    <NavItem key={item.id}>
                      {item.href ? (
                        <NavLink
                          as="a"
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          $isActive={false}
                          $isCollapsed={isCollapsed}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <NavIconWrapper>
                            <Icon name={item.icon} size={20} />
                          </NavIconWrapper>
                          <NavLabel $isCollapsed={isCollapsed}>{item.label}</NavLabel>
                        </NavLink>
                      ) : (
                        <NavLink
                          $isActive={activeTab === item.id}
                          $isCollapsed={isCollapsed}
                          onClick={() => handleNavigation(item.id)}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <NavIconWrapper>
                            <Icon name={item.icon} size={20} />
                          </NavIconWrapper>
                          <NavLabel $isCollapsed={isCollapsed}>{item.label}</NavLabel>
                        </NavLink>
                      )}
                    </NavItem>
                  ))}
                </NavList>
                )}
              </NavGroup>
            );
          })}
        </NavContent>
      </NavContainer>
    </>
  );
};
